import { useCallback, useEffect, useState } from 'react'

import { SearchSection } from '#/shared/api/generated/communication/models'
import { useEscapeStack } from '#/shared/lib/hooks'

import { useSearchTerm } from './search'
import { useConversationSearch } from './use-conversation-search'
import type { ConversationSearchResult } from './use-conversation-search'

export interface InChatSearch {
  /** The search box has taken the thread header over. */
  open: boolean
  /** Raw input, so the field stays controlled while the term debounces. */
  input: string
  /** The debounced term actually queried, or undefined while it's too short. */
  query: string | undefined
  /** Hits inside this thread only. The chats section is never asked for — the
   *  only thread it could return is the one already open. */
  result: ConversationSearchResult
  setInput: (value: string) => void
  close: () => void
  toggle: () => void
}

/**
 * Search within the open thread: the same split-search endpoint scoped to one
 * conversation, so a hit can be opened at its place in this thread's history.
 * Only available for an id-scoped thread — a number-opened one (calls tab,
 * favorites) has no conversation id to scope by.
 *
 * `active` mirrors the thread's own fetch gate, so nothing is queried behind a
 * closed panel.
 */
export function useInChatSearch(
  conversationId: string | undefined,
  active: boolean,
): InChatSearch {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const query = useSearchTerm(input)

  // Leaving the thread (or losing the conversation id) closes the box, so a
  // reopened thread never starts on someone else's search.
  useEffect(() => {
    setOpen(false)
    setInput('')
  }, [conversationId])

  const close = useCallback(() => {
    setOpen(false)
    setInput('')
  }, [])
  // Esc closes the search before the panel's own back-out handler sees it — the
  // shared stack resolves by recency, and this registers deeper.
  useEscapeStack(open, close)

  const result = useConversationSearch(
    query,
    open && active && !!conversationId,
    { conversationId, section: SearchSection.messages },
  )

  return {
    open,
    input,
    query,
    result,
    setInput,
    close,
    toggle: useCallback(() => (open ? close() : setOpen(true)), [open, close]),
  }
}
